/**
 * Neutral on-brand fallback avatar — used when a character's uploaded image fails
 * to load (404 from production redeploys, broken URLs, etc.) or is missing entirely.
 *
 * Returns a data: URL with an SVG containing a unique gradient + the character's
 * first letter. Never 404s. Never shows the old "scary mecha robot" placeholder.
 */

// Curated palette — gradient stops keyed off the character name's hash so each
// character gets a stable, distinct colour scheme.
const PALETTE = [
  ['#F472B6', '#7C3AED'], // pink → violet
  ['#F59E0B', '#E11D48'], // amber → rose
  ['#06B6D4', '#3B82F6'], // cyan → blue
  ['#10B981', '#22C55E'], // emerald → green
  ['#A855F7', '#EC4899'], // purple → pink
  ['#F97316', '#EF4444'], // orange → red
  ['#6366F1', '#8B5CF6'], // indigo → violet
  ['#14B8A6', '#0EA5E9'], // teal → sky
];

function hashCode(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Returns a base64 data: URL containing an SVG fallback for the given name.
 * Works in any <img src="...">.
 */
export function fallbackAvatarUrl(name = '') {
  const [c1, c2] = PALETTE[hashCode(name || 'animus') % PALETTE.length];
  const text = initials(name);
  const id = `g${hashCode(name) % 100000}`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 500" preserveAspectRatio="xMidYMid slice">
  <defs>
    <linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${c1}"/>
      <stop offset="100%" stop-color="${c2}"/>
    </linearGradient>
    <radialGradient id="${id}_g" cx="0.5" cy="0.35" r="0.7">
      <stop offset="0%" stop-color="rgba(255,255,255,0.25)"/>
      <stop offset="100%" stop-color="rgba(0,0,0,0)"/>
    </radialGradient>
  </defs>
  <rect width="400" height="500" fill="url(#${id})"/>
  <rect width="400" height="500" fill="url(#${id}_g)"/>
  <text x="200" y="285" text-anchor="middle"
        font-family="Inter, system-ui, sans-serif"
        font-size="170" font-weight="900" fill="rgba(255,255,255,0.95)"
        letter-spacing="-4">${text}</text>
</svg>`;
  // unicode-safe base64 (handles accented names)
  const b64 =
    typeof window === 'undefined'
      ? Buffer.from(svg, 'utf-8').toString('base64')
      : window.btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${b64}`;
}
